/**
 * Analytics Hooks
 *
 * React hooks for analytics tracking.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics as useAnalyticsService } from '../providers/AnalyticsProvider';
import type {
  ErrorContext,
  EventProperties,
  PageViewProperties,
  UserTraits,
} from '../services/analytics.service';

// Re-export the main hook from provider
export { useAnalytics, useAnalyticsEnabled } from '../providers/AnalyticsProvider';

// ═══════════════════════════════════════════════════════════════════════
// PAGE VIEW TRACKING
// ═══════════════════════════════════════════════════════════════════════

/**
 * Options for useTrackPageView hook
 */
export interface TrackPageViewOptions {
  /** Additional properties to include with the page view */
  properties?: PageViewProperties;
  /** Only track once, even if dependencies change */
  once?: boolean;
  /** Skip tracking if condition is false */
  enabled?: boolean;
}

/**
 * Automatically track page views on mount
 *
 * @param pageName - Name of the page being viewed
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * // Basic usage
 * function DashboardPage() {
 *   useTrackPageView('Dashboard');
 *   return <div>Dashboard content</div>;
 * }
 *
 * // With properties
 * function ProductPage({ productId }) {
 *   useTrackPageView('Product', {
 *     properties: { productId },
 *   });
 *   return <div>Product content</div>;
 * }
 *
 * // Track only once
 * function SettingsPage() {
 *   useTrackPageView('Settings', { once: true });
 *   return <div>Settings content</div>;
 * }
 *
 * // Conditional tracking
 * function ProtectedPage({ isAuthenticated }) {
 *   useTrackPageView('Protected', { enabled: isAuthenticated });
 *   return <div>Protected content</div>;
 * }
 * ```
 */
export function useTrackPageView(pageName: string, options: TrackPageViewOptions = {}): void {
  const analytics = useAnalyticsService();
  const { properties, once = false, enabled = true } = options;
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (once && hasTracked.current) return;

    analytics.trackPageView(pageName, properties);
    hasTracked.current = true;
  }, [analytics, pageName, properties, once, enabled]);
}

// ═══════════════════════════════════════════════════════════════════════
// EVENT TRACKING
// ═══════════════════════════════════════════════════════════════════════

/**
 * Returns a memoized function to track events
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const trackEvent = useTrackEvent();
 *
 *   const handleSubmit = () => {
 *     trackEvent('form_submitted', { formName: 'contact' });
 *   };
 *
 *   return <button onClick={handleSubmit}>Submit</button>;
 * }
 * ```
 */
export function useTrackEvent(): (name: string, properties?: EventProperties) => void {
  const analytics = useAnalyticsService();

  return useCallback(
    (name: string, properties?: EventProperties) => {
      analytics.trackEvent(name, properties);
    },
    [analytics]
  );
}

/**
 * Returns a memoized function to track a specific event
 *
 * @example
 * ```tsx
 * function AddToCartButton({ productId, price }) {
 *   const trackAddToCart = useTrackEventCallback('add_to_cart', {
 *     productId,
 *     price,
 *   });
 *
 *   return <button onClick={trackAddToCart}>Add to Cart</button>;
 * }
 * ```
 */
export function useTrackEventCallback(eventName: string, properties?: EventProperties): () => void {
  const analytics = useAnalyticsService();

  return useCallback(() => {
    analytics.trackEvent(eventName, properties);
  }, [analytics, eventName, properties]);
}

// ═══════════════════════════════════════════════════════════════════════
// ERROR TRACKING
// ═══════════════════════════════════════════════════════════════════════

/**
 * Returns a memoized function to track errors
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const trackError = useTrackError();
 *
 *   const handleApiError = (error: Error) => {
 *     trackError(error, { component: 'MyComponent', action: 'fetchData' });
 *   };
 *
 *   // Use in try-catch
 *   try {
 *     await fetchData();
 *   } catch (error) {
 *     handleApiError(error as Error);
 *   }
 * }
 * ```
 */
export function useTrackError(): (error: Error, context?: ErrorContext) => void {
  const analytics = useAnalyticsService();

  return useCallback(
    (error: Error, context?: ErrorContext) => {
      analytics.trackError(error, context);
    },
    [analytics]
  );
}

// ═══════════════════════════════════════════════════════════════════════
// USER IDENTIFICATION
// ═══════════════════════════════════════════════════════════════════════

/**
 * Returns functions for user identification
 *
 * @example
 * ```tsx
 * function AuthProvider({ children }) {
 *   const { identify, reset } = useAnalyticsIdentify();
 *
 *   const handleLogin = async (credentials) => {
 *     const user = await login(credentials);
 *     identify(user.id, {
 *       email: user.email,
 *       name: user.name,
 *       plan: user.plan,
 *     });
 *   };
 *
 *   const handleLogout = async () => {
 *     await logout();
 *     reset();
 *   };
 *
 *   return <AuthContext.Provider value={{ handleLogin, handleLogout }}>{children}</AuthContext.Provider>;
 * }
 * ```
 */
export function useAnalyticsIdentify(): {
  identify: (userId: string, traits?: UserTraits) => void;
  reset: () => void;
} {
  const analytics = useAnalyticsService();

  const identify = useCallback(
    (userId: string, traits?: UserTraits) => {
      analytics.identify(userId, traits);
    },
    [analytics]
  );

  const reset = useCallback(() => {
    analytics.reset();
  }, [analytics]);

  return { identify, reset };
}

/**
 * Automatically identify user when userId changes
 *
 * @example
 * ```tsx
 * function App() {
 *   const { user } = useAuth();
 *
 *   // Automatically identify when user logs in
 *   useIdentifyUser(user?.id, {
 *     email: user?.email,
 *     role: user?.role,
 *   });
 *
 *   return <div>App content</div>;
 * }
 * ```
 */
export function useIdentifyUser(userId: string | null | undefined, traits?: UserTraits): void {
  const analytics = useAnalyticsService();

  useEffect(() => {
    if (userId) {
      analytics.identify(userId, traits);
    } else {
      analytics.reset();
    }
  }, [analytics, userId, traits]);
}

// ═══════════════════════════════════════════════════════════════════════
// SUPER PROPERTIES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Set super properties that will be included with every event
 *
 * @example
 * ```tsx
 * function AppWrapper() {
 *   const { user } = useAuth();
 *
 *   // Set properties that should be included in all events
 *   useSuperProperties({
 *     userPlan: user?.plan,
 *     userRole: user?.role,
 *     featureFlags: getFeatureFlags(),
 *   });
 *
 *   return <App />;
 * }
 * ```
 */
export function useSuperProperties(properties: EventProperties): void {
  const analytics = useAnalyticsService();

  useEffect(() => {
    if (properties && Object.keys(properties).length > 0) {
      analytics.setSuperProperties(properties);
    }
  }, [analytics, properties]);
}

// ═══════════════════════════════════════════════════════════════════════
// TIMING HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Track time spent on a page or component
 *
 * @example
 * ```tsx
 * function ArticlePage({ articleId }) {
 *   useTrackTiming('article_read', {
 *     articleId,
 *     category: 'engagement',
 *   });
 *
 *   return <div>Article content</div>;
 * }
 * ```
 */
export function useTrackTiming(eventName: string, properties?: EventProperties): void {
  const analytics = useAnalyticsService();
  const startTime = useRef(Date.now());

  useEffect(() => {
    startTime.current = Date.now();

    return () => {
      const duration = Date.now() - startTime.current;
      analytics.trackEvent(eventName, {
        ...properties,
        durationMs: duration,
        durationSeconds: Math.round(duration / 1000),
      });
    };
  }, [analytics, eventName, properties]);
}

// ═══════════════════════════════════════════════════════════════════════
// CLICK TRACKING
// ═══════════════════════════════════════════════════════════════════════

/**
 * Returns an onClick handler that tracks clicks
 *
 * @example
 * ```tsx
 * function CTAButton() {
 *   const handleClick = useTrackClick('cta_clicked', {
 *     location: 'hero',
 *     variant: 'primary',
 *   });
 *
 *   return <button onClick={handleClick}>Get Started</button>;
 * }
 *
 * // With additional handler
 * function SubmitButton({ onSubmit }) {
 *   const handleClick = useTrackClick('submit_clicked', { form: 'contact' }, onSubmit);
 *
 *   return <button onClick={handleClick}>Submit</button>;
 * }
 * ```
 */
export function useTrackClick(
  eventName: string,
  properties?: EventProperties,
  additionalHandler?: () => void
): () => void {
  const trackEvent = useTrackEvent();

  return useCallback(() => {
    trackEvent(eventName, properties);
    additionalHandler?.();
  }, [trackEvent, eventName, properties, additionalHandler]);
}
